/**
 * 国际跳棋游戏组件
 * 严格按照国际正式比赛规则实现
 */

import React, { useState, useCallback, useEffect } from 'react';
import { GameMode, Difficulty, Player, Position } from '../types/game';
import { CheckersGameState } from '../types/game';
import {
  createInitialGameState,
  selectPiece,
  deselectPiece,
  makeMove,
  checkGameEnd
} from '../utils/checkers';
import { getCheckersAIMove, simulateAIThinking } from '../utils/checkersAI';
import CheckersBoard from './CheckersBoard';
import VictoryEffect from './VictoryEffect';
import GameShareButton from './GameShareButton';
import { cn } from '../lib/utils';

interface CheckersGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
  onGameEnd?: (winner: Player, moves: number) => void;
}

const CheckersGame: React.FC<CheckersGameProps> = ({
  gameMode,
  difficulty = 'medium',
  onBackToMenu,
  onBackToModeSelect,
  onGameEnd
}) => {
  const [gameState, setGameState] = useState<CheckersGameState>(() =>
    createInitialGameState(gameMode, difficulty)
  );
  const [gameMessage, setGameMessage] = useState<string>('黑方先行');
  const [lastWinner, setLastWinner] = useState<Player | null>(null);
  const [showVictoryEffect, setShowVictoryEffect] = useState(false);
  const [winRecord, setWinRecord] = useState({ black: 0, white: 0 });
  const [isBoardEnlarged, setIsBoardEnlarged] = useState<boolean>(false);
  const [showEndGameDialog, setShowEndGameDialog] = useState<boolean>(false);
  const [dialogManuallyClosed, setDialogManuallyClosed] = useState<boolean>(false);
  const [showRules, setShowRules] = useState<boolean>(false);

  // 播放胜利音效
  const playVictorySound = (winner: Player) => {
    try {
      const audio = new Audio(winner === 'black' ? '/sounds/win-black.mp3' : '/sounds/win-white.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (error) {
      console.log('音效播放失败:', error);
    }
  };

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        if (gameState.gameHistory.length > 0) {
          const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
          setGameState(previousState as CheckersGameState);
          setGameMessage('');
        }
      }
      if (event.key === 'r' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        const newState = createInitialGameState(gameMode, difficulty);
        setGameState(newState);
        setGameMessage('黑方先行');
        setShowVictoryEffect(false);
      }
      if (event.key === 'e' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        setIsBoardEnlarged(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState.gameHistory, gameMode, difficulty]);

  // 切换棋盘大小
  const toggleBoardEnlarge = () => {
    setIsBoardEnlarged(prev => !prev);
  };

  // 重置游戏
  const handleReset = useCallback(() => {
    console.log('Resetting game...');
    const newState = createInitialGameState(gameMode, difficulty);
    setGameState(newState);
    setGameMessage('黑方先行');
    setShowVictoryEffect(false);
    setShowEndGameDialog(false);
    setDialogManuallyClosed(false);
  }, [gameMode, difficulty]);

  // 处理棋盘点击
  const handleCellClick = useCallback((position: Position) => {
    console.log('CheckersGame: Cell clicked', position);

    if (gameState.status !== 'playing' || gameState.isAIThinking) {
      console.log('Click ignored - game status:', gameState.status, 'AI thinking:', gameState.isAIThinking);
      return;
    }

    const board = gameState.board;
    const clickedPiece = board[position.row][position.col];
    console.log('Clicked piece:', clickedPiece, 'Current player:', gameState.currentPlayer);

    // 如果已选中棋子，尝试移动
    if (gameState.selectedPiece) {
      console.log('Piece already selected:', gameState.selectedPiece);
      const possibleMove = gameState.possibleMoves?.find(
        move => move.to.row === position.row && move.to.col === position.col
      );
      console.log('Possible move found:', possibleMove);

      if (possibleMove) {
        console.log('Executing move:', possibleMove);

        // 执行移动
        const newGameState = makeMove(gameState, possibleMove);
        setGameState(newGameState);
        setGameMessage('');
        console.log('Move executed, new player:', newGameState.currentPlayer);

        // 检查游戏是否结束
        if (newGameState.status === 'finished') {
          setLastWinner(newGameState.winner);
          setShowVictoryEffect(true);
          setShowEndGameDialog(true);
          setWinRecord(prev => ({
            ...prev,
            [newGameState.winner!]: prev[newGameState.winner as 'black' | 'white'] + 1
          }));
          playVictorySound(newGameState.winner!);

          if (onGameEnd) {
            onGameEnd(newGameState.winner!, newGameState.totalMoves);
          }
        } else if (newGameState.selectedPiece && newGameState.possibleMoves && newGameState.possibleMoves.length > 0) {
          // 连续吃子
          console.log('Continuous capture available');
          setGameMessage('连续吃子中...');
        } else {
          console.log('Player turn completed, current player:', newGameState.currentPlayer);
          setGameMessage('');
        }
      } else if (clickedPiece?.player === gameState.currentPlayer) {
        // 选择另一个己方棋子
        console.log('Selecting different piece');
        const newGameState = selectPiece(gameState, position);
        setGameState(newGameState);
      } else {
        // 取消选择
        console.log('Deselecting piece');
        const newGameState = deselectPiece(gameState);
        setGameState(newGameState);
      }
    } else if (clickedPiece?.player === gameState.currentPlayer) {
      // 选择棋子
      console.log('Selecting piece for first time');
      const newGameState = selectPiece(gameState, position);
      console.log('New possible moves:', newGameState.possibleMoves?.length);
      setGameState(newGameState);
    } else {
      console.log('Invalid click - no valid action');
    }
  }, [gameState, onGameEnd]);

  // AI移动逻辑
  useEffect(() => {
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white' && gameState.status === 'playing' && !gameState.isAIThinking) {
      console.log('AI turn started');

      // 设置AI思考状态
      setGameState(prev => ({ ...prev, isAIThinking: true }));
      setGameMessage('AI思考中...');

      // 模拟AI思考时间
      simulateAIThinking(difficulty || 'medium').then(() => {
        console.log('AI calculating move...');
        try {
          const aiMove = getCheckersAIMove(gameState, difficulty || 'medium');
          console.log('AI move calculated:', aiMove);

          if (aiMove) {
            const newGameState = makeMove(gameState, aiMove);
            console.log('AI move executed, new player:', newGameState.currentPlayer);
            setGameState(newGameState);
            setGameMessage('');

            // 检查游戏是否结束
            if (newGameState.status === 'finished') {
              console.log('Game finished, winner:', newGameState.winner);
              setLastWinner(newGameState.winner);
              setShowVictoryEffect(true);
              setShowEndGameDialog(true);
              setWinRecord(prev => ({
                ...prev,
                [newGameState.winner!]: prev[newGameState.winner as 'black' | 'white'] + 1
              }));
              playVictorySound(newGameState.winner!);

              if (onGameEnd) {
                onGameEnd(newGameState.winner!, newGameState.totalMoves);
              }
            } else if (newGameState.selectedPiece && newGameState.possibleMoves && newGameState.possibleMoves.length > 0) {
              // AI连续吃子
              console.log('AI continuous capture available');
              setGameMessage('AI连续吃子中...');
            } else {
              console.log('AI turn completed, switching to player');
            }
          } else {
            console.log('AI has no valid moves');
            setGameMessage('AI无法移动');
            // AI无法移动，游戏结束
            setGameState(prev => ({
              ...prev,
              status: 'finished',
              winner: 'black'
            }));
          }
        } catch (error) {
          console.error('AI move calculation failed:', error);
          setGameMessage('AI出错，跳过回合');
          setGameState(prev => ({ ...prev, isAIThinking: false }));
        }
      });
    } else if (gameState.isAIThinking && (gameMode === 'pvp' || gameState.currentPlayer === 'black')) {
      // 确保AI状态被重置
      setGameState(prev => ({ ...prev, isAIThinking: false }));
      if (gameMode === 'pvp' || gameState.currentPlayer === 'black') {
        setGameMessage('');
      }
    }
  }, [gameMode, gameState.currentPlayer, gameState.status, gameState.isAIThinking, difficulty, onGameEnd]);

  // 胜利特效监听
  useEffect(() => {
    if (gameState.status === 'finished' && gameState.winner && gameState.winner !== lastWinner) {
      setLastWinner(gameState.winner);
      setWinRecord(prev => ({
        ...prev,
        [gameState.winner!]: prev[gameState.winner as 'black' | 'white'] + 1
      }));
      setShowVictoryEffect(true);
      playVictorySound(gameState.winner);
    }
  }, [gameState.status, gameState.winner, lastWinner]);

  // 重新开始游戏
  const handleRestart = useCallback(() => {
    console.log('Restarting game...');
    const newState = createInitialGameState(gameMode, difficulty);
    setGameState(newState);
    setGameMessage('黑方先行');
    setShowVictoryEffect(false);
    setShowEndGameDialog(false);
    setDialogManuallyClosed(false);
  }, [gameMode, difficulty]);

  // 撤销移动
  const handleUndo = useCallback(() => {
    if (gameState.gameHistory.length > 0) {
      console.log('Undoing move...');
      const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
      setGameState(previousState as CheckersGameState);
      setGameMessage('');
    }
  }, [gameState]);

  // 返回菜单
  const handleBackToMenu = useCallback(() => {
    if (onBackToMenu) {
      onBackToMenu();
    }
  }, [onBackToMenu]);

  // 获取当前玩家信息
  const getCurrentPlayerInfo = () => {
    const isBlackTurn = gameState.currentPlayer === 'black';
    return {
      name: isBlackTurn ? '黑方' : '白方',
      color: isBlackTurn ? 'bg-gray-800' : 'bg-white border-2 border-gray-300',
      isAI: gameMode === 'pvc' && !isBlackTurn
    };
  };

  // 获取当前的游戏提示信息
  const getCurrentGameMessage = () => {
    if (gameState.isAIThinking) {
      return gameMessage;
    }

    if (gameState.mustCapture) {
      return '⚠️ 强制吃子！必须选择可以吃子的棋子';
    }

    if (gameState.isCapturing) {
      return '🔄 连续吃子中...请继续选择吃子移动';
    }

    if (gameState.status === 'playing' && !gameState.isAIThinking) {
      return '💡 选择己方棋子查看可用移动';
    }

    return gameMessage || '游戏进行中';
  };

  const currentPlayerInfo = getCurrentPlayerInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">♟️</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-yellow-600 bg-clip-text text-transparent sm:mb-1">
                国际跳棋
              </h1>
              <span className="text-2xl">🏁</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            {onBackToMenu && (
              <button
                onClick={onBackToMenu}
                className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium"
                title="回到首页"
              >
                <span>🏠</span>
                <span className="hidden sm:inline">首页</span>
              </button>
            )}

            {/* 返回按钮 */}
            {onBackToModeSelect && (
              <button
                onClick={onBackToModeSelect}
                className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium"
                title="返回模式选择"
              >
                <span>🔙</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button
              onClick={handleRestart}
              className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium"
              title="重新开始"
            >
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button
              onClick={handleUndo}
              disabled={gameState.gameHistory.length === 0 || gameState.status !== 'playing' || gameState.isAIThinking}
              className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                gameState.gameHistory.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title="悔棋"
            >
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 棋盘大小切换按钮 */}
            <button
              onClick={toggleBoardEnlarge}
              className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                isBoardEnlarged
                  ? 'bg-purple-500 text-white hover:bg-purple-600'
                  : 'bg-gray-400 text-white hover:bg-gray-500'
              }`}
              title={isBoardEnlarged ? "缩小棋盘 (Ctrl+E)" : "放大棋盘 (Ctrl+E)"}
            >
              <span>{isBoardEnlarged ? '🔍' : '🔎'}</span>
              <span className="hidden sm:inline">{isBoardEnlarged ? '缩小' : '放大'}</span>
            </button>

            {/* 游戏规则按钮 */}
            <button
              onClick={() => setShowRules(true)}
              className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium"
              title="游戏规则"
            >
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="checkers" />
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      {gameState.status === 'playing' && (
        <div className="bg-white shadow p-2">
          <div className="flex items-center justify-between">
            {/* 当前玩家信息 */}
            <div className="flex items-center space-x-1 sm:space-x-1">
              <div className={`w-4 h-4 rounded-full ${
                gameState.currentPlayer === 'black' ? 'bg-gray-800' : 'bg-gray-100 border-2 border-gray-400'
              }`}></div>
              <span className="text-sm sm:text-base font-medium">
                {gameState.currentPlayer === 'black' ? '黑方' : '白方'}回合
              </span>
              {gameState.isAIThinking && (
                <span className="text-xs text-blue-600 animate-pulse ml-2">AI思考中...</span>
              )}
              {gameState.mustCapture && (
                <span className="text-xs text-red-600 ml-2">强制吃子</span>
              )}
              {gameState.isCapturing && (
                <span className="text-xs text-orange-600 ml-2">连续吃子</span>
              )}
            </div>

            {/* 步数信息 */}
            <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
              <span>步数: {gameState.totalMoves}</span>
              <span>黑棋: {gameState.blackPiecesCount}</span>
              <span>白棋: {gameState.whitePiecesCount}</span>
            </div>
          </div>
        </div>
      )}

      {/* 游戏统计信息 - 统一布局 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center justify-center space-x-4 sm:space-x-6 text-sm sm:text-base">
          <div className="text-center">
            <span className="text-gray-700 font-medium">吃子: </span>
            <span className="font-bold text-gray-900">{gameState.captureMoves}</span>
          </div>
          <div className="text-center">
            <span className="text-gray-700 font-medium">升级: </span>
            <span className="font-bold text-gray-900">{gameState.promotionMoves}</span>
          </div>
          <div className="text-center">
            <span className="text-gray-700 font-medium">战绩: </span>
            <span className="font-bold text-gray-900">
              黑{winRecord.black}胜 - 白{winRecord.white}胜
            </span>
          </div>
        </div>
      </div>

      {/* 棋盘 - 移动端满屏布局 */}
      <div className="flex-1 flex items-center justify-center px-0 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full flex items-center justify-center">
          <CheckersBoard
            gameState={gameState}
            onCellClick={handleCellClick}
            disabled={gameState.status !== 'playing' || gameState.isAIThinking}
            isZoomed={isBoardEnlarged}
            gameMode={gameMode}
          />
        </div>
      </div>

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-amber-50 border-t border-amber-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-amber-800 text-center leading-relaxed">
          <strong>规则：</strong>
          黑方先行 | 斜向移动 | 普通棋子只能前进 | 到达底线升级为王棋（可后退） | 必须吃子时可连续吃子 | 吃光对方棋子获胜
          {gameMode === 'pvc' ? ' | 人机对战' : ' | 双人对战'}
        </p>
      </div>

      {/* 胜利特效 */}
      {showVictoryEffect && lastWinner && (
        <VictoryEffect
          isVisible={showVictoryEffect}
          winner={lastWinner}
          onHide={() => setShowVictoryEffect(false)}
        />
      )}

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 右上角关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>

            <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center pr-8">
              <span className="text-2xl mr-2">📖</span>
              国际跳棋游戏规则
            </h2>

            <div className="text-gray-700 space-y-3 text-sm">
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🎯 游戏目标</h3>
                <p>通过吃掉对手所有棋子或让对手无法移动来获胜。</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🎮 游戏规则</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>棋盘为10×10，每位玩家20个棋子</li>
                  <li>普通棋子只能向前斜进一格</li>
                  <li>跳过对方棋子吃子（可连续吃子）</li>
                  <li>到达对方底线升级为王棋</li>
                  <li>王棋可向前向后斜进移动</li>
                  <li>必须吃子，无法移动则输棋</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">💡 策略提示</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>控制棋盘中心位置</li>
                  <li>保护后方棋子不被突破</li>
                  <li>优先创造连续吃子机会</li>
                  <li>王棋威力巨大，尽快升级</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🔧 操作提示</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>点击己方棋子选中，再点击目标位置移动</li>
                  <li>蓝色高亮显示可移动位置</li>
                  <li>使用"悔棋"按钮撤销上一步</li>
                  <li>使用"重开"按钮重新开始游戏</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowRules(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 游戏结束对话框 - 统一布局 */}
      {showEndGameDialog && lastWinner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl relative">
            {/* 关闭按钮 */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowEndGameDialog(false);
                setDialogManuallyClosed(true);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-center mb-4">
              <span className={lastWinner === 'black' ? 'text-gray-800' : 'text-gray-600'}>
                {lastWinner === 'black' ? '黑方' : '白方'}获胜！
              </span>
            </h2>

            <div className="text-center text-gray-600 mb-6">
              <p>游戏统计：</p>
              <p>总步数: {gameState.totalMoves}</p>
              <p>吃子数: {gameState.captureMoves}</p>
              <p>升级数: {gameState.promotionMoves}</p>
              {gameMode === 'pvc' && (
                <p>AI难度: {difficulty === 'easy' ? '简单' : difficulty === 'medium' ? '中等' : '困难'}</p>
              )}
              <p>最终战绩: 黑{winRecord.black}胜 - 白{winRecord.white}胜</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleRestart}
                className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                再来一局
              </button>
              {onBackToMenu && (
                <button
                  onClick={handleBackToMenu}
                  className="w-full bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  返回主页
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckersGame;